/**
 * Can be either a element already found or a string that match an id for loading one element from dom 
 */
export type ElementDef = string | HTMLElement;

export class PageTools {
  ptversion = '1.4.1';
  errorid = "error";
  errortoconsole = true;
  errortoelement = true;
  
  constructor(){
  }
  
  getversion(): Object {return {'ptversion': this.ptversion};};

  /**
   * Call this function when document have finished loading
   * @param fn a function to run
   */
  ready(fn: EventListener | (() => void)): void {
    if (document.readyState !== 'loading') {
      let e = new Event('DOMContentLoaded');
      fn(e);
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  /**
   * Find one HtlmElement
   * @param elmid a existing element or a string that will find a element by id
   * @param selector uses to find a element inside the element found by elmid
   * @returns HtmlElement or null
   */
  elm(elmid: ElementDef, selector?: string): HTMLElement | null {
    if (!elmid) return null; 
    let el: HTMLElement | null = null;
    if (typeof elmid != 'string'){
      if (selector!=undefined)
        el = elmid.querySelector(selector);
      else
        el = elmid;
    } else {
      el = document.getElementById(elmid);
      if (el && selector!=undefined)
        el = el.querySelector(selector);
    }
    return el;
  }

  /**
   * Get inner htlm to or from a element
   * @param elmid a existing element or a string that will find a element by id
   * @returns the inner htlm of the element or null if element not found
   */
  html(elmid: ElementDef): string | null;
  /**
   * Set inner htlm to an element
   * @param elmid a existing element or a string that will find a element by id
   * @param code if given a string, it will be set to the element
   * @returns null if element not found or the element if code set
   */
  html(elmid: ElementDef, code: string): null | HTMLElement;
  /**
   * Set or get inner htlm to or from a element
   * @param elmid a existing element or a string that will find a element by id
   * @param code if given a string, it will be set to the element
   * @returns null if element not found,  the inner htlm of the element if code is not given and the element if code given 
   */
  html(elmid: ElementDef, code?:string): string | null | HTMLElement {
    let el = this.elm(elmid);
    if (el){
      if (typeof code == 'string'){
        el.innerHTML = code;
        return el;
      } else
        return el.innerHTML;
    } 
    return null;
  }

  /**
   * Get 0 or more elements from selector(s) or element and selector
   * @param sel1 existing element to be queried for children or a selector for selecting elemenets
   * @param sel2 
   * @returns list of Elements or null
   */
  elms(sel1: ElementDef, sel2?: string): NodeList | null {
    if (sel2 === undefined && typeof sel1 === 'string') // on document
      return document.querySelectorAll(sel1);
    else if (sel1){
      let sel = this.elm(sel1);
      if (sel && typeof sel2 === 'string')
        return sel.querySelectorAll(sel2);
    }
    return null;
  }

  /**
   * Create new element from html code
   * @param code html code
   * @returns elements created from the html
   */
  createelms(code: string): HTMLCollection | null {
    if (typeof code == 'string'){
      const template = document.createElement('template');
      template.innerHTML = code.trim();
      return template.content.children;
    }
    return code;
  }

  /**
   * Append children created by html code to the end of the selected element
   * @param elmid a existing element or a string that will find a element by id
   * @param code html code for creating new elements
   * @returns the first of the created elements or undefined if none created
   */
  appendto(elmid: ElementDef, code: string): HTMLElement | null {
    let first = null;
    let el = this.elm(elmid);
    if (el){
      let toappend = this.createelms(code);
      if (toappend){
        while(toappend.length>0){ // looks like everytime a element is appended to el one dissapare from element list
          let ae = toappend.item(0);
          if (first == null && ae != null)
            first = ae as HTMLElement;
          if (ae) el.append(ae);
        }
      }
    }
    return first;
  }

  /**
   * Remove children from the back of thil element till there is only the amount of to left
   * @param elmid an existing element or a string that will find a element by id
   * @param to hove many children to be left, default is 0 if not set
   */
  removefrom(elmid: ElementDef, to?: number): void {
    let el = this.elm(elmid);
    if (to === undefined)
      to = 0;
    if (el && el.children && el.children.length>0){
      for (let i=el.children.length; i>to; i--){
        const child=el.children[i-1];
        if (child) child.remove();
      }
    }
  }

  /**
   * Add a class to all selected elements
   * @param selector a selector that will match elements in the DOM
   * @param cname class name to add
   */
  selectoraddclass(selector: string, cname: string): void {
    let elms = this.elms(selector);
    if (elms){
      for (let idx = 0; idx<elms.length; idx++){
        const el = elms.item(idx);
        if (el instanceof HTMLElement)
          el.classList.add(cname);
      }
    }
  }

  /**
   * Remove class from all selected elements
   * @param selector a selector that will match elements in the DOM
   * @param cname class name to remove
   */
  selectorremoveclass(selector: string, cname: string): void {
    let elms = this.elms(selector);
    if (elms){
      for (let idx = 0; idx<elms.length; idx++) {
        const el = elms.item(idx);
        if (el instanceof HTMLElement)
          el.classList.remove(cname);
      }
    }
  }

  /**
   * Return a attribute value if it exists
   * @param elmid an existing element or a string that will find a element by id
   * @param attr attribute name
   * @returns attribute value or blank string
   */
  getattrval(elmid: ElementDef, attr: string): string;
  /**
   * Return a attribute value if it exists
   * @param elmid an existing element or a string that will find a element by id
   * @param attr attribute name
   * @param nullonnotfound if set to true a null will be returned for not found
   * @returns attribute value or blank or null depending on nullonnotfound
   */
  getattrval(elmid: ElementDef, attr: string, nullonnotfound: true): string | null;
  /**
   * Return a attribute value if it exists
   * @param elmid an existing element or a string that will find a element by id
   * @param attr attribute name
   * @param nullonnotfound if set to true a null will be returned for not found
   * @returns attribute value or blank or null depending on nullonnotfound
   */
  getattrval(elmid: ElementDef, attr: string, nullonnotfound?: boolean): string | null {
    let el: HTMLElement | null  = this.elm(elmid);
    let retval: string | null = '';
    if (nullonnotfound)
      retval = null;
    try {
      if (el){ 
        let attrnode = el.attributes.getNamedItem(attr);
        if (attrnode)
          retval = attrnode.nodeValue;
      }
    } catch (error) {}
    return retval;
  }
  
  /**
   * Set a attribute value on a element or if value is null, remove it
   * @param elmid an existing element or a string that will find a element by id
   * @param attr attribute name
   * @param val a string value to set or null if you want to remove the attribute
   * @returns element if found and attribute set or null if element not found or remove failed
   */
  setattrval(elmid: ElementDef, attr: string, val: string): HTMLElement | null {
    let el: HTMLElement | null  = this.elm(elmid);
    if (el){
       if (val != null){
        el.setAttribute(attr, val);
      } else if (val == null){ // means remove
        const attrnode = el.attributes.getNamedItem(attr); // check if exists beore remove
        if (attrnode) el.attributes.removeNamedItem(attr);
        else el = null; // not exist then return null
      }
    }
    return el;
  }

  /**
   * Find attribute on element or on the first child that have this attribute and return the value
   * @param elid an existing element or a string that will find a element by id
   * @param attname name of the attribure to find
   * @returns attribute or null
   */
  findfirstattrval(elid: ElementDef, attname: string): string | null{
    let el = typeof elid === 'string' ? this.elm(elid) : elid;
    let val = null;
    if (el){
      val = this.getattrval(el,attname,true);
      if (val == null && el && el.children){
        for (let idx = 0; idx<el.children.length;idx++){
          let child = el.children[idx];
          if (child  && child instanceof HTMLElement){
            val = this.findfirstattrval(child, attname);
            if (val != null)
              break;
          }
        }
      }
    }
    return val;
  }

  /**
   * Check for attribute on element or children and return the first element having the attributre defined
   * @param elid an existing element or a string that will find a element by id
   * @param attname name of the attribure to find
   * @returns element with attributre defined or null
   */
  findfirstwithattr(elid: ElementDef, attname: string): HTMLElement | null {
    let el = typeof elid === 'string' ? this.elm(elid) : elid;
    let retel = null;
    let val = null;
    if (el) {
      this.getattrval(el,attname,true);
      if (val == null){
        if (el.children && el.children.length>0){
          for (let idx = 0; idx<el.children.length;idx++){
            let child = el.children[idx];
            if (child  && child instanceof HTMLElement){
              let cret = this.findfirstwithattr(child,attname);
              if (cret){
                val = this.getattrval(cret,attname,true);
                if (val != null){
                  retel = cret;
                  break;
                }
              }
            }
          }        
        }
      } else {
        retel = el;
      }
    }
    return retel;
  }

  /**
   * Get value from element
   * @param elmid an existing element or a string that will find a element by id
   * @returns return the value of element as string or boolean if the element exists and null if not
  */
  val(elmid: ElementDef): string | boolean | null ;
  /**
   * Set value in element
   * @param elmid an existing element or a string that will find a element by id
   * @param val this is the value to set
   * @returns it will return undefined if a value is set and null if element do not exist
  */
  val(elmid: ElementDef, val: string | boolean):  null | undefined;
  /**
   * Set or get value from element
   * @param elmid an existing element or a string that will find a element by id
   * @param val if parameter set, this is the value to set
   * @returns return the value of element as string or boolean if the element exists and null if not
   * or if we are setting value if will return undefined if a value is set and null if element do not exist
  */
  val(elmid: ElementDef, val?: string | boolean): string | boolean | null | undefined {
    let el =  this.elm(elmid);
    if (el === null)
      return null;
    if (val === undefined) {// read value
      if (el instanceof HTMLSelectElement){
        for (let idx = 0; idx<el.options.length; idx++){
          let option = el.options[idx];
          if (option && option.selected)
            return option.value;
        }
        return "";
      }
      else if (el instanceof HTMLInputElement) {
        switch (el.type){
          case 'radio':
            return el.checked ? true : false;
          case 'checkbox':
            return el.checked ? true : false;
          default:
            return el.value;
        }
      } else if (el instanceof HTMLTextAreaElement) {
        return el.value;
      }
      return null;
    } else { // set value
      if (el instanceof HTMLSelectElement) {
        for (let idx = 0; idx<el.options.length; idx++){
          let option = el.options[idx];
          if (option) {
            if (option.value == val)
              option.selected = true;
            else
              option.selected = false;
          }
        }
      } else if (el instanceof HTMLInputElement) {
        switch (el.type){
          case 'radio':
            el.checked = val ? true : false;
            break;
          case 'checkbox':
            el.checked = val ? true : false;
            break;
          default:
            el.value = typeof val === 'string' ? val : '';
        }
      } else if (el instanceof HTMLTextAreaElement) {
        el.value = typeof val === 'string' ? val : '';
      }
      return undefined;
    }
  }

  /**
   * Hide a element by adding d-none class
   * @param elmid an existing element or a string that will find a element by id
   * @returns the element or null
   */
  hide(elmid: ElementDef): HTMLElement | null {
    let el =  this.elm(elmid);
    if (el === null)
      return el;
    el.classList.add("d-none");
    return el;
  }

  /**
   * Show a element by remove d-none class
   * @param elmid an existing element or a string that will find a element by id
   * @returns the element or null
   */
  show(elmid: ElementDef): HTMLElement | null {
    let el =  this.elm(elmid);
    if (el === null)
      return el;
    el.classList.remove("d-none");
    return el;
  }

  /**
   * Call a method when element selected is clicked
   * @param elmid an existing element or a string that will find a element by id
   * @param fn a method in the class extended on this class
   */
  onclickme(elmid: ElementDef, fn: EventListener): void {
    let el = this.elm(elmid);
    if (el && fn){
      el.onclick = fn.bind(this);
    }
  }

  /**
   * Call a method when element selected is changed
   * @param elmid an existing element or a string that will find a element by id
   * @param fn a method in the class extended on this class
   */
  onchangeme(elmid: ElementDef, fn: EventListener): void {
    let el = this.elm(elmid);
    if (el && fn){
      el.onchange = fn.bind(this);
    }
  }

  /**
   * Call a method when element get the named event. This method if for handleing all events 
   * that not are so commonly used that they have got their own method like onclickme
   * @param elmid an existing element or a string that will find a element by id
   * @param eventname the name for the event to register ex input, focusin, focusout 
   * @param fn a method in the class extended on this class
   */
  oneventme(elmid: ElementDef, eventname:string, fn: EventListener): void {
    let el = this.elm(elmid);
    if (el && fn){
      el.addEventListener(eventname, fn.bind(this));
    }
  }
  
  /**
   * Test if a instance is a real object and not array or null
   * @param x to test
   * @returns true if real object
   */
  isobject(x: any): boolean {
    return (typeof x === 'object' && !Array.isArray(x) && x !== null);
  }

  /**
   * Test if a instance is a real array and not object or null
   * @param x to test
   * @returns true if real array
   */
  isarray(x: any): boolean{
    return (typeof x === 'object' && Array.isArray(x) && x !== null);
  }

  senderror(result: any): void {
      let msg = (result.error ? result.error : result.message);
      if (result.errorcode) msg = '(' + result.errorcode + ')' + msg;
      if (this.errortoconsole) console.error(msg);
      if (this.errortoelement) this.html(this.errorid,msg);
  }

  /**
   * Do a GET request to the url and return the json object
   * @param url an url that is allowed to use
   * @returns a real object from call to server or a {result: 'error', error: message}
   */
  async doGet(url: string): Promise<Object> {
    let result;

    try {
      const response = await fetch(url);
      result = await response.json();
    } catch (error) {
      if (error instanceof Error) {
        result = {'result' : 'failed', 'error' : error.message};
      } else
        result = {'result' : 'failed', 'error' : error};
    }
    if (result.result != 'ok') this.senderror(result);
    return result;
  }

  /**
   * Do a DELETE request to the url and return the json object
   * @param url an url that is allowed to use
   * @returns a real object from call to server or a {result: 'error', error: message}
   */
  async doDelete(url: string): Promise<Object> {
    let result;

    try {
      const response = await fetch(url, { method: "DELETE"});
      result = await response.json();
    } catch (error) {
      if (error instanceof Error) {
        result = {'result' : 'failed', 'error' : error.message};
      } else
        result = {'result' : 'failed', 'error' : error};
    }
    if (result.result != 'ok') this.senderror(result);
    return result;
  }

  /**
   * Do a PUT request to the url and return the json object
   * @param url an url that is allowed to use
   * @param indata a object that will be stringified to send 
   * @returns a real object from call to server or a {result: 'error', error: message} 
   */
  async doPut(url: string, indata: Object): Promise<Object> {
    let result;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(indata)                
      });
      result = await response.json();
    } catch (error) {
      if (error instanceof Error) {
        result = {'result' : 'failed', 'error' : error.message};
      } else
        result = {'result' : 'failed', 'error' : error};
    }
    if (result.result != 'ok') this.senderror(result);
    return result;
  }

  /**
   * Do a POST request to the url and return the json object
   * @param url an url that is allowed to use
   * @param indata a object that will be stringified to send 
   * @returns a real object from call to server or a {result: 'error', error: message} 
   */
  async doPost(url: string, indata: Object): Promise<Object> {
    let result;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(indata)                
      });
      result = await response.json();
    } catch (error) {
      if (error instanceof Error) {
        result = {'result' : 'failed', 'error' : error.message};
      } else
        result = {'result' : 'failed', 'error' : error};
    }
    if (result.result != 'ok') this.senderror(result);
    return result;
  }
}
